import { useCallback, useEffect, useMemo, useRef } from "react";

// The server emits a turn's terminator (tse:idle / tse:question) BEFORE it
// commits the turn to storage, so a join issued the instant the terminator
// lands can read storage without it. Each retry waits one step longer.
export const REHYDRATE_DELAY_MS = 800;
export const REHYDRATE_MAX_ATTEMPTS = 3;

const PHASE_NONE = "none";
const PHASE_WAITING_TURN = "waiting_turn";
const PHASE_REHYDRATING = "rehydrating";
// cancel() after the re-join went out: its replay and idle still arrive and must not touch the new turn.
const PHASE_DISCARD = "discard";

const hasFiniteCost = (payload) => Number.isFinite(Number(payload?.session_cost_usd));

/**
 * A tab that (re)joins while the session's turn is still running.
 *
 * The join ack says `turn_in_flight: true`, but everything that turn emitted
 * before the join is gone for this tab: tse:replay carries committed turns
 * only, so the reloaded / switched-back tab paints a partial (or empty) answer
 * from the chunks that are still to come — and no thinking row until then.
 *
 *   noteJoin        the ack; true when the caller should raise the thinking state.
 *   consumeJoinIdle the join's own composer-release tse:idle (no cost fields),
 *                   sent right after the replay; swallowed so it does not cancel
 *                   the thinking state the ack just raised.
 *   consumeJoinQuestion true for a question answering a discarded re-join: drop it.
 *   noteTurnEnd     the in-flight turn ended: re-join once it has committed, so
 *                   the replay (a REPLACE) paints the finished turn exactly once.
 *   acceptReplay    false for a re-hydrate replay that still lacks the turn
 *                   (commit not landed yet): keep what is on screen, retry.
 *   cancel          the user started a turn; a late REPLACE would wipe it. A re-join
 *                   already on the wire has its replay dropped and its idle swallowed.
 *   reset           forget everything (clear, disconnect): that join's reply is not coming.
 */
export default function useInFlightRejoin(rejoin) {
  const phaseRef = useRef(PHASE_NONE);
  const baseTurnsRef = useRef(NaN);
  const attemptsRef = useRef(0);
  const swallowIdleRef = useRef(false);
  const timerRef = useRef(null);
  const rejoinSentRef = useRef(false);
  const rejoinRef = useRef(rejoin);
  rejoinRef.current = rejoin;

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    phaseRef.current = PHASE_NONE;
    swallowIdleRef.current = false;
    rejoinSentRef.current = false;
    attemptsRef.current = 0;
  }, [clearTimer]);

  const cancel = useCallback(() => {
    if (!rejoinSentRef.current) {
      reset();
      return;
    }
    clearTimer();
    attemptsRef.current = 0;
    phaseRef.current = PHASE_DISCARD;
    swallowIdleRef.current = true;
  }, [clearTimer, reset]);

  const leaveDiscardWhenDone = () => {
    if (phaseRef.current === PHASE_DISCARD && !rejoinSentRef.current && !swallowIdleRef.current) {
      phaseRef.current = PHASE_NONE;
    }
  };

  const schedule = useCallback(() => {
    clearTimer();
    const delay = REHYDRATE_DELAY_MS * (attemptsRef.current + 1);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      rejoinSentRef.current = true;
      rejoinRef.current?.();
    }, delay);
  }, [clearTimer]);

  const noteJoin = useCallback((payload, { streaming = false } = {}) => {
    // Our own re-hydrate join: the replay decides, the ack must not re-arm
    // (when discarded, the turn in flight is the engineer's new one).
    if (phaseRef.current === PHASE_REHYDRATING || phaseRef.current === PHASE_DISCARD) return false;
    // Finished while this tab was away: the replay riding with this ack already has it.
    if (payload?.turn_in_flight === false) {
      reset();
      return false;
    }
    if (payload?.turn_in_flight !== true || streaming) return false;
    phaseRef.current = PHASE_WAITING_TURN;
    baseTurnsRef.current = Number(payload?.turn_count);
    attemptsRef.current = 0;
    swallowIdleRef.current = true;
    return true;
  }, [reset]);

  // Any idle ends the join's window; only a costless one (the join's own) is swallowed.
  const consumeJoinIdle = useCallback((payload) => {
    if (!swallowIdleRef.current) return false;
    swallowIdleRef.current = false;
    leaveDiscardWhenDone();
    return !hasFiniteCost(payload);
  }, []);

  // A discarded re-join can be answered with a re-sent question instead of an idle:
  // drop it (the engineer already moved on) and end the discard.
  const consumeJoinQuestion = useCallback(() => {
    if (phaseRef.current !== PHASE_DISCARD) return false;
    reset();
    return true;
  }, [reset]);

  const noteTurnEnd = useCallback(() => {
    if (phaseRef.current !== PHASE_WAITING_TURN) return;
    phaseRef.current = PHASE_REHYDRATING;
    attemptsRef.current = 0;
    schedule();
  }, [schedule]);

  const acceptReplay = useCallback((payload) => {
    if (phaseRef.current === PHASE_DISCARD) {
      rejoinSentRef.current = false;
      leaveDiscardWhenDone();
      return false;
    }
    if (phaseRef.current !== PHASE_REHYDRATING || timerRef.current) return true;
    rejoinSentRef.current = false;
    const count = Array.isArray(payload?.turns) ? payload.turns.length : 0;
    const base = baseTurnsRef.current;
    if (!Number.isFinite(base) || count > base) {
      reset();
      return true;
    }
    attemptsRef.current += 1;
    if (attemptsRef.current < REHYDRATE_MAX_ATTEMPTS) schedule();
    else reset();
    return false;
  }, [reset, schedule]);

  useEffect(() => clearTimer, [clearTimer]);

  return useMemo(
    () => ({ noteJoin, consumeJoinIdle, consumeJoinQuestion, noteTurnEnd, acceptReplay, cancel, reset }),
    [noteJoin, consumeJoinIdle, consumeJoinQuestion, noteTurnEnd, acceptReplay, cancel, reset],
  );
}
